import { useCallback, useMemo, useState } from 'react'
import { CombinedGraphQLErrors, isErrorLike } from '@apollo/client/errors'
import { useLingui } from '@lingui/react/macro'
import { FieldError, FieldErrors, FieldValues } from 'react-hook-form'

import { Props as RootProps } from '../components/Root'

interface Props {
  mapper?: (field: string, message: string) => [string, FieldError] | undefined
}

type HandlePromiseCatchFn = (
  error: unknown,
  opts?: {
    unknownString?: string
    customErrors?: Record<string, { key: string; message: string }>
  }
) => void

interface Result<T extends FieldValues = FieldValues> {
  parseApolloErrors: (error: unknown) => void
  setServerError: (type: string, message: string) => void
  handlePromiseCatch: HandlePromiseCatchFn
  errors: RootProps<T>['serverErrors']
}

export function useErrorHelpers<T extends FieldValues = FieldValues>({
  mapper = (field, message) => [
    field,
    {
      type: 'default',
      message
    }
  ]
}: Props): Result<T> {
  const { t } = useLingui()

  const [globalError, setGlobalError] = useState<FieldError>()
  const [serverErrors, setServerErrors] = useState<FieldErrors<T>>()

  const parseApolloErrors = useCallback(
    (error: unknown) => {
      // In Apollo Client 4, check if error is CombinedGraphQLErrors
      if (!CombinedGraphQLErrors.is(error)) return

      const graphQLErrors = error.errors
      const errors = graphQLErrors
        .map(function (error) {
          if (!error.extensions) return null
          if (!('field' in error.extensions)) return null

          const field = error.extensions.field

          if (typeof field !== 'string') return null
          return mapper(field, error.message)
        })
        .filter(field => field != null)

      setServerErrors(Object.fromEntries(errors) as FieldErrors<T>)
    },
    [mapper, setServerErrors]
  )

  const setServerError = useCallback(
    (type: string, message: string) => {
      setGlobalError({
        message,
        type
      })
    },
    [setGlobalError]
  )

  const handlePromiseCatch = useCallback<HandlePromiseCatchFn>(
    (error, opts) => {
      const unknownString = opts?.unknownString ?? t`Unknown error`
      const customErrors = opts?.customErrors ?? {}

      if (CombinedGraphQLErrors.is(error)) {
        parseApolloErrors(error)

        return
      }

      if (isErrorLike(error)) {
        if (error.message in customErrors) {
          setServerError(
            customErrors[error.message].key,
            customErrors[error.message].message
          )

          return
        }

        setServerError(error.name, error.message)

        return
      }

      setServerError('unknown', unknownString)
    },
    [setServerError, parseApolloErrors, t]
  )

  const errors = useMemo(() => {
    let errors: FieldErrors<T> = {}

    if (serverErrors) {
      errors = { ...serverErrors }
    }

    if (globalError) {
      errors.root = {
        serverError: globalError
      }
    }

    return errors
  }, [serverErrors, globalError])

  return {
    parseApolloErrors,
    setServerError,
    handlePromiseCatch,
    errors
  }
}
